import React, { useState, useMemo } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, PieChart, Pie, Cell, ReferenceLine
} from 'recharts';
import { FarmingPackage } from '@shared/types';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useCurrency } from '@/hooks/use-currency';

const COLORS = ['#111827', '#4b5563', '#9ca3af', '#e5e7eb'];
export function AdvancedROIProjector({ pkg }: { pkg: FarmingPackage }) {
  const [isSeasonal, setIsSeasonal] = useState(false);
  const { formatFromBase } = useCurrency();
  const projectionData = useMemo(() => {
    const months = ['M1', 'M2', 'M3', 'M4', 'M5', 'M6', 'M7', 'M8', 'M9', 'M10', 'M11', 'M12'];
    const monthlyProfit = pkg.projectedAnnualProfit / 12;
    let cumulative = -pkg.totalCost;
    return months.map((m, i) => {
      // In seasonal mode, profit only hits in specific months (e.g., M4, M8, M12)
      const isHarvestMonth = i === 3 || i === 7 || i === 11;
      const profitValue = isSeasonal 
        ? (isHarvestMonth ? monthlyProfit * 4 : 0) 
        : (i >= 2 ? monthlyProfit : 0);
      cumulative += profitValue;
      return {
        name: m,
        Profit: Math.round(cumulative)
      };
    });
  }, [pkg, isSeasonal]);
  const costBreakdown = useMemo(() => {
    return pkg.items.map(item => ({
      name: item.product.subcategory,
      value: item.subtotal
    })).reduce((acc: any[], curr) => {
      const existing = acc.find(a => a.name === curr.name);
      if (existing) existing.value += curr.value;
      else acc.push(curr);
      return acc;
    }, []);
  }, [pkg]);
  return (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-background p-5 rounded-md border shadow-sm space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-bold uppercase tracking-tight">Financial Timeline</h3>
            <div className="flex items-center space-x-2">
              <Switch id="seasonal-toggle" checked={isSeasonal} onCheckedChange={setIsSeasonal} />
              <Label htmlFor="seasonal-toggle" className="text-xs">Harvest Cycle View</Label>
            </div>
          </div>
          <div className="h-[200px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={projectionData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10 }} />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10 }}
                  tickFormatter={(value) => formatFromBase(Number(value), { maximumFractionDigits: 0 })}
                />
                <Tooltip
                  formatter={(value: number) => formatFromBase(value)}
                  contentStyle={{ borderRadius: '4px', border: '1px solid #e5e7eb', fontSize: '10px' }}
                />
                <ReferenceLine y={0} stroke="#111827" strokeDasharray="3 3" />
                <Area
                  type="monotone"
                  dataKey="Profit"
                  stroke="hsl(var(--primary))"
                  fill="hsl(var(--primary))"
                  fillOpacity={0.05}
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <p className="text-[10px] text-muted-foreground">
            Break-even estimated at Month {pkg.breakEvenMonth}. ROI: {pkg.roiPercentage}%
          </p>
        </div>
        <div className="bg-background p-5 rounded-md border shadow-sm space-y-4">
          <h3 className="text-sm font-bold uppercase tracking-tight">Investment Allocation</h3>
          <div className="h-[200px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={costBreakdown}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={70}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {costBreakdown.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} strokeWidth={0} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend iconType="square" wrapperStyle={{ fontSize: '10px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}